import { ChangeEvent, useId, useState } from "react";

import "./style.scss";
import {
    BoundaryMode,
    CrawlSchedule,
    ReviewPolicy
} from "../../common/interfaces/admin/crawl.interface";
import {
    BOUNDARY_LABELS,
    CrawlSettingsValues,
    REVIEW_POLICY_LABELS,
    SCHEDULE_LABELS,
    seedHost
} from "./crawl-settings.util";

/**
 * The crawl settings fields shared by the "Add Source → Web site" form and
 * the "Edit crawl settings" dialog (web_scraper_provider_design.md BR-34,
 * §10). Native labeled inputs (DESIGN_GUIDE §6 style 1); the host component
 * owns the values, validation and the submit row.
 */
export const CrawlSettingsForm = ({
    values,
    onChange,
    mode,
    disabled = false
}: {
    values: CrawlSettingsValues;
    onChange: (values: CrawlSettingsValues) => void;
    mode: "create" | "edit";
    disabled?: boolean;
}) => {
    const id = useId();
    const [showAdvanced, setShowAdvanced] = useState(
        mode === "edit" &&
            (!!values.excludePatterns || !!values.includePattern || values.robotsOverride)
    );
    const host = seedHost(values);

    const set = <K extends keyof CrawlSettingsValues>(key: K, value: CrawlSettingsValues[K]) =>
        onChange({ ...values, [key]: value });
    const text =
        (key: keyof CrawlSettingsValues) =>
        (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
            set(key, evt.target.value as never);
    const check = (key: keyof CrawlSettingsValues) => (evt: ChangeEvent<HTMLInputElement>) =>
        set(key, evt.target.checked as never);

    return (
        <div className="crawl-settings">
            <div className="crawl-settings__field">
                <label htmlFor={`${id}-seeds`}>Start URLs (one per line)</label>
                <textarea
                    id={`${id}-seeds`}
                    rows={2}
                    placeholder="https://www.example.org/"
                    value={values.seedUrls}
                    onChange={text("seedUrls")}
                    disabled={disabled}
                    aria-describedby={`${id}-seeds-hint`}
                />
                <span className="crawl-settings__hint" id={`${id}-seeds-hint`}>
                    The crawl starts here and follows links within the site boundary below.
                </span>
            </div>

            <div className="crawl-settings__field">
                <label htmlFor={`${id}-boundary`}>Stay within</label>
                <select
                    id={`${id}-boundary`}
                    value={values.boundaryMode}
                    onChange={evt => set("boundaryMode", evt.target.value as BoundaryMode)}
                    disabled={disabled}
                >
                    {(Object.keys(BOUNDARY_LABELS) as BoundaryMode[]).map(mode => (
                        <option key={mode} value={mode}>
                            {BOUNDARY_LABELS[mode]}
                        </option>
                    ))}
                </select>
            </div>
            {values.boundaryMode === "prefixes" && (
                <div className="crawl-settings__field">
                    <label htmlFor={`${id}-prefixes`}>URL prefixes (one per line)</label>
                    <textarea
                        id={`${id}-prefixes`}
                        rows={2}
                        placeholder="https://www.example.org/policies/"
                        value={values.boundaryPrefixes}
                        onChange={text("boundaryPrefixes")}
                        disabled={disabled}
                    />
                </div>
            )}

            <div className="crawl-settings__row">
                <div className="crawl-settings__field">
                    <label htmlFor={`${id}-depth`}>
                        Depth <span className="crawl-settings__default">(default 5)</span>
                    </label>
                    <input
                        id={`${id}-depth`}
                        type="number"
                        min={1}
                        inputMode="numeric"
                        value={values.maxDepth}
                        onChange={text("maxDepth")}
                        disabled={disabled}
                    />
                </div>
                <div className="crawl-settings__field">
                    <label htmlFor={`${id}-pages`}>
                        Pages <span className="crawl-settings__default">(default 500)</span>
                    </label>
                    <input
                        id={`${id}-pages`}
                        type="number"
                        min={1}
                        inputMode="numeric"
                        value={values.maxPages}
                        onChange={text("maxPages")}
                        disabled={disabled}
                    />
                </div>
                <div className="crawl-settings__field">
                    <label htmlFor={`${id}-documents`}>
                        Documents <span className="crawl-settings__default">(default 200)</span>
                    </label>
                    <input
                        id={`${id}-documents`}
                        type="number"
                        min={1}
                        inputMode="numeric"
                        value={values.maxDocuments}
                        onChange={text("maxDocuments")}
                        disabled={disabled}
                    />
                </div>
                <div className="crawl-settings__field">
                    <label htmlFor={`${id}-minutes`}>
                        Minutes <span className="crawl-settings__default">(default 30)</span>
                    </label>
                    <input
                        id={`${id}-minutes`}
                        type="number"
                        min={1}
                        inputMode="numeric"
                        value={values.maxRunMinutes}
                        onChange={text("maxRunMinutes")}
                        disabled={disabled}
                    />
                </div>
            </div>
            <span className="crawl-settings__hint">
                Limits above your organization&apos;s ceiling are rejected when you save.
            </span>

            <div className="crawl-settings__checks">
                <div className="crawl-settings__check">
                    <input
                        type="checkbox"
                        id={`${id}-documents-on`}
                        checked={values.includeDocuments}
                        onChange={check("includeDocuments")}
                        disabled={disabled}
                    />
                    <label htmlFor={`${id}-documents-on`}>
                        Include linked documents (PDF, Word, Excel, PowerPoint)
                    </label>
                </div>
                <div className="crawl-settings__check">
                    <input
                        type="checkbox"
                        id={`${id}-media-on`}
                        checked={values.includeMedia}
                        onChange={check("includeMedia")}
                        disabled={disabled}
                    />
                    <label htmlFor={`${id}-media-on`}>Include audio and video files</label>
                </div>
            </div>

            <div className="crawl-settings__row crawl-settings__row--stacked">
                <div className="crawl-settings__field">
                    <label htmlFor={`${id}-schedule`}>Refresh schedule</label>
                    <select
                        id={`${id}-schedule`}
                        value={values.crawlSchedule}
                        onChange={evt => set("crawlSchedule", evt.target.value as CrawlSchedule)}
                        disabled={disabled}
                    >
                        {(Object.keys(SCHEDULE_LABELS) as CrawlSchedule[]).map(schedule => (
                            <option key={schedule} value={schedule}>
                                {SCHEDULE_LABELS[schedule]}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="crawl-settings__field">
                    <label htmlFor={`${id}-review`}>Review policy</label>
                    <select
                        id={`${id}-review`}
                        value={values.reviewPolicy}
                        onChange={evt => set("reviewPolicy", evt.target.value as ReviewPolicy)}
                        disabled={disabled}
                        aria-describedby={`${id}-review-hint`}
                    >
                        {(Object.keys(REVIEW_POLICY_LABELS) as ReviewPolicy[]).map(policy => (
                            <option key={policy} value={policy}>
                                {REVIEW_POLICY_LABELS[policy]}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <span className="crawl-settings__hint" id={`${id}-review-hint`}>
                The first crawl always stops for review before anything is indexed. Later refreshes
                that remove many pages, or that could not finish, stop for review too.
            </span>

            <div className="crawl-settings__field">
                <label htmlFor={`${id}-notify`}>
                    Email crawl results to (one address per line, optional)
                </label>
                <textarea
                    id={`${id}-notify`}
                    rows={2}
                    placeholder="webmaster@example.org"
                    value={values.notifyEmails}
                    onChange={text("notifyEmails")}
                    disabled={disabled}
                    aria-describedby={`${id}-notify-hint`}
                />
                <span className="crawl-settings__hint" id={`${id}-notify-hint`}>
                    Each address gets a short summary when a crawl finishes: whether it is waiting
                    for review, was applied automatically, was held because many pages disappeared,
                    or failed.
                </span>
            </div>

            <div className="crawl-settings__advanced">
                <button
                    type="button"
                    className="crawl-settings__advanced-toggle"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    aria-expanded={showAdvanced}
                >
                    {showAdvanced ? "▾" : "▸"} Advanced settings
                </button>
                {showAdvanced && (
                    <div className="crawl-settings__advanced-content">
                        <div className="crawl-settings__field">
                            <label htmlFor={`${id}-exclude`}>
                                Exclude URL patterns (regular expressions, one per line)
                            </label>
                            <textarea
                                id={`${id}-exclude`}
                                rows={3}
                                placeholder={
                                    mode === "create" ? "Standard exclusions apply when empty" : ""
                                }
                                value={values.excludePatterns}
                                onChange={text("excludePatterns")}
                                disabled={disabled}
                                aria-describedby={`${id}-exclude-hint`}
                            />
                            <span className="crawl-settings__hint" id={`${id}-exclude-hint`}>
                                {mode === "create"
                                    ? "Leave empty for the standard exclusions (tag, category and author archives, searches, carts, logins, feeds, print views, paged listings)."
                                    : "Matched against the full page address. Patterns excluded during a review are added here when you choose to save them."}
                            </span>
                        </div>
                        <div className="crawl-settings__field">
                            <label htmlFor={`${id}-include`}>
                                Only include URLs matching (optional)
                            </label>
                            <input
                                id={`${id}-include`}
                                type="text"
                                placeholder="e.g. /policies/|/handbook/"
                                value={values.includePattern}
                                onChange={text("includePattern")}
                                disabled={disabled}
                            />
                        </div>
                        <div className="crawl-settings__checks">
                            <div className="crawl-settings__check">
                                <input
                                    type="checkbox"
                                    id={`${id}-sitemap`}
                                    checked={values.useSitemap}
                                    onChange={check("useSitemap")}
                                    disabled={disabled}
                                />
                                <label htmlFor={`${id}-sitemap`}>Use the site&apos;s sitemap</label>
                            </div>
                            <div className="crawl-settings__check">
                                <input
                                    type="checkbox"
                                    id={`${id}-follow`}
                                    checked={values.followLinks}
                                    onChange={check("followLinks")}
                                    disabled={disabled}
                                />
                                <label htmlFor={`${id}-follow`}>Follow links between pages</label>
                            </div>
                            <div className="crawl-settings__check">
                                <input
                                    type="checkbox"
                                    id={`${id}-robots`}
                                    checked={values.robotsOverride}
                                    onChange={evt =>
                                        onChange({
                                            ...values,
                                            robotsOverride: evt.target.checked,
                                            robotsAttested: evt.target.checked
                                                ? values.robotsAttested
                                                : false
                                        })
                                    }
                                    disabled={disabled}
                                    aria-describedby={`${id}-robots-hint`}
                                />
                                <label htmlFor={`${id}-robots`}>
                                    Ignore the site&apos;s robots.txt rules
                                </label>
                                <span className="crawl-settings__hint" id={`${id}-robots-hint`}>
                                    Only for a site your organization operates. The override is
                                    recorded with your name and the time.
                                </span>
                            </div>
                            {values.robotsOverride && (
                                <div className="crawl-settings__check crawl-settings__check--attest">
                                    <input
                                        type="checkbox"
                                        id={`${id}-attest`}
                                        checked={values.robotsAttested}
                                        onChange={check("robotsAttested")}
                                        disabled={disabled}
                                    />
                                    <label htmlFor={`${id}-attest`}>
                                        I confirm that {host || "this site"} is operated by my
                                        organization and that crawling it without regard to
                                        robots.txt is authorized.
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CrawlSettingsForm;
